import { Injectable, computed, inject, signal } from "@angular/core";
import { Auth } from "@angular/fire/auth";
import { NavigationEnd, Router } from "@angular/router";
import { filter } from "rxjs/operators";
import { Organization } from "../models/organization.model";
import { OrganizationsService } from "../services/organizations.service";
import { environment } from "../../environments/environment";
import { PermissionsService } from "../services/permissions.service";

export interface NavItem {
	label: string;
	icon: string;
	route: string | unknown[];
	exact?: boolean;
}

const RAIL_COLLAPSED_KEY = "mch.rail.collapsed";

/**
 * Reserved segments directly under /organizations that are routes rather than
 * organization ids. Without this, /organizations/new would put the rail into
 * organization mode for an organization that does not exist yet.
 */
const NON_ID_SEGMENTS = new Set(["new"]);

/** Pure so it can be tested without a router. */
export function organizationIdFromUrl(url: string): string | null {
	const path = url.split(/[?#]/)[0];
	const segments = path.split("/").filter(Boolean);

	if (segments[0] !== "organizations" || segments.length < 2) {
		return null;
	}

	const candidate = segments[1];
	return NON_ID_SEGMENTS.has(candidate) ? null : candidate;
}

@Injectable({ providedIn: "root" })
export class ShellNavService {
	private readonly router = inject(Router);
	private readonly auth = inject(Auth);
	private readonly permissions = inject(PermissionsService);
	private readonly organizations = inject(OrganizationsService);

	private readonly url = signal(this.router.url);
	private readonly hasGlobalAccess = signal(false);
	private readonly hasOrganizationAccess = signal(false);
	private readonly hasIconsAccess = signal(false);
	private readonly organizationList = signal<Organization[]>([]);

	readonly organizationId = computed(() => organizationIdFromUrl(this.url()));
	readonly mode = computed<"global" | "organization">(() =>
		this.organizationId() ? "organization" : "global",
	);

	readonly switchableOrganizations = this.organizationList.asReadonly();

	private readonly railCollapsedState = signal(this.readCollapsed());
	readonly railCollapsed = this.railCollapsedState.asReadonly();

	readonly globalItems = computed<NavItem[]>(() => {
		const items: NavItem[] = [];

		if (this.hasGlobalAccess()) {
			items.push({ label: "Overview", icon: "dashboard", route: "/overview" });
		}
		if (this.hasOrganizationAccess() || this.hasGlobalAccess()) {
			items.push({ label: "Organizations", icon: "business", route: "/organizations" });
		}
		if (this.hasGlobalAccess()) {
			items.push(
				{ label: "All Users", icon: "people", route: "/users" },
				{
					label: "Global Admins",
					icon: "admin_panel_settings",
					route: "/global-admins",
				},
				{
					label: "Mail Delivery Stats",
					icon: "mark_email_read",
					route: "/mail-delivery-stats",
				},
			);
		}
		if (this.hasIconsAccess()) {
			items.push({ label: "Icons", icon: "emoji_symbols", route: "/icons" });
		}

		return items;
	});

	readonly organizationItems = computed<NavItem[]>(() => {
		const id = this.organizationId();
		if (!id) {
			return [];
		}

		return [
			{ label: "Dashboard", icon: "dashboard", route: ["/organizations", id, "dashboard"] },
			{
				label: "Admins",
				icon: "admin_panel_settings",
				route: ["/organizations", id, "admins"],
			},
			{ label: "Respondents", icon: "people", route: ["/organizations", id, "respondents"] },
			{
				label: "User Imports",
				icon: "upload_file",
				route: ["/organizations", id, "user-imports"],
			},
			{ label: "Surveys", icon: "assignment", route: ["/organizations", id, "surveys"] },
			{
				label: "Mail Delivery Stats",
				icon: "mark_email_read",
				route: ["/organizations", id, "mail-delivery-stats"],
			},
		];
	});

	constructor() {
		this.router.events
			.pipe(filter((event) => event instanceof NavigationEnd))
			.subscribe(() => this.url.set(this.router.url));

		void this.loadAccess();
	}

	toggleRail(): void {
		const next = !this.railCollapsedState();
		this.railCollapsedState.set(next);
		try {
			localStorage.setItem(RAIL_COLLAPSED_KEY, String(next));
		} catch {
			// A browser refusing storage is not a reason to break navigation.
		}
	}

	private readCollapsed(): boolean {
		try {
			return localStorage.getItem(RAIL_COLLAPSED_KEY) === "true";
		} catch {
			return false;
		}
	}

	/**
	 * Mirrors exactly what the old header did: isGlobalAdmin for global access,
	 * a non-empty organization access list for organization access, and the
	 * noldor.co.za email check for the developer icons page.
	 */
	private async loadAccess(): Promise<void> {
		const user = this.auth.currentUser;
		if (!user) {
			return;
		}

		// Development only, and only for us. The route carries the same
		// environment gate — hiding a rail item does not close its URL.
		this.hasIconsAccess.set(
			!environment.production &&
				(user.email?.toLowerCase().endsWith("@noldor.co.za") ?? false),
		);

		const isGlobalAdmin = await this.permissions.isGlobalAdmin(user.uid);
		this.hasGlobalAccess.set(isGlobalAdmin);

		const orgAccess = await this.permissions.getAllOrganizationAccess(user.uid);
		this.hasOrganizationAccess.set(orgAccess.length > 0);

		// A global admin can reach every organization; anyone else only their own.
		const source$ = isGlobalAdmin
			? this.organizations.getAllOrganizations()
			: this.organizations.getAllOrganizationsForUser(user.uid);

		source$.subscribe((organizations) => this.organizationList.set(organizations));
	}
}
